import {
  Button,
  cn,
  IconButton,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RangeCalendar,
} from "@beweegbaas/ui";
import { useAtomSet } from "@effect/atom-react";
import { parseDate, type CalendarDate } from "@internationalized/date";
import { useState, type ReactNode } from "react";
import type { SchooljaarRange } from "#/modules/les.planning/domain/calendar";
import type { Roosterregel } from "#/modules/les.planning/domain/rooster";
import {
  checkVakantie,
  checkVrijeDag,
  countRoosterLessen,
  formatDate,
  formatDateRange,
  formatShortDateRange,
  formatShortVrijeDag,
  formatVrijeDag,
  getPeriodeduur,
  getSchooljaarDays,
  getVakantieEindDays,
  getVrijePeriodes,
  MAX_NAAM_LENGTH,
  type DateError,
  type PeriodCheck,
  type Vakantie,
  type Vakantieregio,
  type VakantiesOverzicht,
  type VrijeDag,
} from "#/modules/les.planning/domain/vakanties";
import {
  addVrijeDagAtom,
  changeVakantieAtom,
  changeVrijeDagAtom,
} from "#/modules/les.planning/vakanties/vakanties-editor";

interface Period {
  readonly start: CalendarDate;
  readonly eind: CalendarDate;
}

/** The dates of a schoolvakantie as a button; they change in a calendar over the list. */
export function VakantiePeriode({
  vakantie,
  vakantieregio,
  range,
}: {
  vakantie: Vakantie;
  vakantieregio: Vakantieregio;
  range: SchooljaarRange;
}) {
  const change = useAtomSet(changeVakantieAtom);
  const advies = { start: parseDate(vakantie.adviesStart), eind: parseDate(vakantie.adviesEind) };
  const value = { start: parseDate(vakantie.start), eind: parseDate(vakantie.eind) };
  return (
    <PeriodeKnop
      label={m["planning.vakanties.aanpassen"]({ naam: vakantie.naam })}
      value={value}
      text={formatDateRange(value.start, value.eind)}
      shortText={formatShortDateRange(value.start, value.eind)}
      days={{ min: getSchooljaarDays(range).min, max: getVakantieEindDays(range).max }}
      check={(start, eind) => checkVakantie(start, eind, range)}
      footer={(_, setDraft) => (
        <p className="text-muted-foreground m-0 flex flex-wrap items-baseline gap-x-2 text-xs">
          {m["planning.vakanties.advies"]({
            regio: vakantieregio,
            datums: formatDateRange(advies.start, advies.eind),
          })}
          <Button
            type="button"
            variant="link"
            className="h-auto px-0 text-xs font-semibold"
            onClick={() => setDraft(advies)}
          >
            {m["planning.vakanties.advies_gebruiken"]()}
          </Button>
        </p>
      )}
      onConfirm={({ start, eind }) =>
        change({ vakantie, start: start.toString(), eind: eind.toString() })
      }
    />
  );
}

/**
 * A vrije dag as a row of the list: its name to type in, its dates as a button and the way to
 * remove it. Without a vrije dag it is a new one, added once it has a name and dates.
 */
export function VrijeDagRij({
  vrijeDag,
  overzicht,
  regels,
  range,
  onDelete,
  onDone,
}: {
  vrijeDag?: VrijeDag;
  overzicht: VakantiesOverzicht;
  regels: readonly Roosterregel[];
  range: SchooljaarRange;
  onDelete: () => void;
  /** A new vrije dag is added. */
  onDone?: () => void;
}) {
  const add = useAtomSet(addVrijeDagAtom, { mode: "promise" });
  const change = useAtomSet(changeVrijeDagAtom, { mode: "promise" });
  const [naam, setNaam] = useState(vrijeDag?.naam ?? "");
  const [period, setPeriod] = useState<Period | null>(() =>
    vrijeDag ? { start: parseDate(vrijeDag.start), eind: parseDate(vrijeDag.eind) } : null,
  );
  const [pending, setPending] = useState(false);
  // The server's overlap, when the overview here missed the period it falls in.
  const [overlap, setOverlap] = useState<string | null>(null);
  const periodes = getVrijePeriodes(overzicht, vrijeDag?.id);

  async function save(next: { naam: string; period: Period | null }) {
    const trimmed = next.naam.trim();
    if (!next.period || trimmed === "" || pending) return;
    const input = {
      naam: trimmed,
      start: next.period.start.toString(),
      eind: next.period.eind.toString(),
    };
    const unchanged =
      vrijeDag &&
      input.naam === vrijeDag.naam &&
      input.start === vrijeDag.start &&
      input.eind === vrijeDag.eind;
    if (unchanged) return;
    setPending(true);
    try {
      const refused = vrijeDag ? await change({ vrijeDag, input }) : await add(input);
      setOverlap(refused);
      if (refused === null) onDone?.();
    } catch {
      // The error toast already showed.
    } finally {
      setPending(false);
    }
  }

  const naamLabel = vrijeDag
    ? m["planning.vakanties.naam_van"]({ naam: vrijeDag.naam })
    : m["planning.vakanties.naam"]();
  return (
    <div className="flex flex-col">
      <div className="flex h-10 items-center gap-3 text-sm sm:h-11">
        <Input
          aria-label={naamLabel}
          value={naam}
          placeholder={m["planning.vakanties.naam_voorbeeld"]()}
          maxLength={MAX_NAAM_LENGTH}
          autoComplete="off"
          autoFocus={!vrijeDag}
          className={cn(
            "h-8 w-34 shrink-0 font-medium shadow-none sm:w-45",
            // Pulled left so its name lines up with those above, and as much wider so its dates do.
            vrijeDag &&
              "hover:border-input border-transparent px-2 -ml-2 w-36 focus-visible:border-ring sm:w-47",
          )}
          onValueChange={(next) => setNaam(next)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          onBlur={() => {
            if (vrijeDag && naam.trim() === "") setNaam(vrijeDag.naam);
            else void save({ naam, period });
          }}
        />
        <PeriodeKnop
          label={
            vrijeDag
              ? m["planning.vakanties.aanpassen"]({ naam: vrijeDag.naam })
              : m["planning.vakanties.kies_dagen"]()
          }
          value={period}
          text={
            period
              ? formatVrijeDag(period.start, period.eind)
              : m["planning.vakanties.kies_dagen"]()
          }
          shortText={period ? formatShortVrijeDag(period.start, period.eind) : undefined}
          days={getSchooljaarDays(range)}
          check={(start, eind) => checkVrijeDag(start, eind, range, periodes)}
          footer={(draft) => (
            <>
              <p className="text-muted-foreground m-0 text-xs">
                {m["planning.vakanties.een_dag"]()}
              </p>
              {draft ? (
                <VervallenLessen
                  aantal={countRoosterLessen(regels, draft.start, draft.eind, periodes)}
                  start={draft.start}
                  eind={draft.eind}
                />
              ) : null}
            </>
          )}
          onConfirm={(next) => {
            setPeriod(next);
            setOverlap(null);
            void save({ naam, period: next });
          }}
        />
        <IconButton
          size="icon-xs"
          label={
            vrijeDag
              ? m["planning.vakanties.verwijderen"]({ naam: vrijeDag.naam })
              : m["common.actions.cancel"]()
          }
          className="hover:bg-destructive/10 hover:text-destructive ml-auto size-7 shrink-0"
          onClick={onDelete}
        >
          <IconMaterialSymbolsCloseRounded className="size-4" />
        </IconButton>
      </div>
      {overlap ? (
        <p role="alert" className="text-destructive m-0 pb-2 text-xs">
          {overlap}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A period as a button that opens a calendar over the list, so nothing around it moves. The
 * calendar starts from the period there is; Klaar takes the new one.
 */
function PeriodeKnop({
  label,
  value,
  text,
  shortText = text,
  days,
  check,
  footer,
  onConfirm,
}: {
  label: string;
  value: Period | null;
  text: string;
  /** What a phone shows, which has no room for the year. */
  shortText?: string;
  days: { readonly min: CalendarDate; readonly max: CalendarDate };
  check: (start: CalendarDate | null, eind: CalendarDate | null) => PeriodCheck;
  footer?: (draft: Period | null, setDraft: (draft: Period) => void) => ReactNode;
  onConfirm: (period: Period) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Period | null>(value);
  const checked = draft ? check(draft.start, draft.eind) : null;
  const error = checked ? (checked.start ?? checked.eind) : null;
  const period = checked?.period ?? null;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setDraft(value);
        setOpen(next);
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={label}
            className={cn(
              "min-w-0 shrink justify-start font-sans font-normal tabular-nums",
              !value && "text-muted-foreground",
            )}
          >
            <IconMaterialSymbolsCalendarMonthOutline aria-hidden />
            <span className="truncate max-sm:hidden">{text}</span>
            <span className="truncate sm:hidden">{shortText}</span>
          </Button>
        }
      />
      <PopoverContent align="start" showArrow={false} className="w-80 gap-3" aria-label={label}>
        <RangeCalendar
          aria-label={label}
          firstDayOfWeek="mon"
          value={draft ? { start: draft.start, end: draft.eind } : null}
          minValue={days.min}
          maxValue={days.max}
          isInvalid={error !== null}
          onChange={(next) => {
            if (next) setDraft({ start: next.start, eind: next.end });
          }}
        />
        {error ? (
          <p role="alert" className="text-destructive m-0 text-xs">
            {describe(error)}
          </p>
        ) : null}
        <div className="flex flex-col gap-2 border-t pt-3">
          {footer?.(period, setDraft)}
          <Button
            type="button"
            size="sm"
            className="self-end"
            disabled={period === null}
            onClick={() => {
              if (!period) return;
              onConfirm(period);
              setOpen(false);
            }}
          >
            {m["planning.vakanties.klaar"]()}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const describe = (error: DateError) => {
  switch (error.kind) {
    case "outOfRange":
      return m["planning.vakanties.buiten_bereik"]({
        van: formatDate(error.range.min),
        tot: formatDate(error.range.max),
      });
    case "beforeStart":
      return m["planning.vakanties.voor_van"]({ datum: formatDate(error.start) });
    case "overlap":
      return m["planning.api_errors.vrije_dag_overlapt"]({ periode: error.naam });
  }
};

/** How many lessen of the rooster the vrije dag takes away; nothing when it takes none. */
function VervallenLessen({
  aantal,
  start,
  eind,
}: {
  aantal: number;
  start: CalendarDate;
  eind: CalendarDate;
}) {
  if (aantal === 0) return null;
  const duur = getPeriodeduur(start, eind);
  const vervallen =
    duur === "dag"
      ? m["planning.vakanties.vervallen_dag"]({ count: aantal })
      : duur === "week"
        ? m["planning.vakanties.vervallen_week"]({ count: aantal })
        : m["planning.vakanties.vervallen_dagen"]({ count: aantal });
  return (
    <p className="bg-primary/6 m-0 flex gap-2 squircle rounded-lg supports-squircle:rounded-2xl px-3 py-2.5 text-[13px] leading-snug">
      <IconMaterialSymbolsInfoOutline aria-hidden className="text-primary mt-px size-4 shrink-0" />
      <span>
        {vervallen} {m["planning.vakanties.blijft_staan"]()}
      </span>
    </p>
  );
}
